// Comprobante / Boleta de Pago en PDF — Grupo Vértice CR
// Inspirado en el formato típico de comprobantes CR; paleta neutral (gris-tierra).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { computeRecordsHours, buildCCSSDayMap, aplicaJornada48, cuentasSalario, repartirValores, ACTIVIDAD_JORNADAS_ESPECIALES } from "@/lib/payroll";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";

// Nombre de la familia tipográfica embebida (DejaVu Sans subset con soporte Unicode incl. ₡)
const FONT_FAMILY = "NomyProSans";

function registerNomyFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

// Paleta neutra
const COLOR_TITLE_BG = [60, 70, 65];     // verde-gris oscuro (cabecera empresa)
const COLOR_TITLE_FG = [255, 255, 255];
const COLOR_LABEL_BG = [232, 230, 225];  // beige claro (etiquetas)
const COLOR_VALUE_BG = [248, 247, 244];  // crema (valores)
const COLOR_HEADER_BG = [225, 225, 220]; // gris claro (sub-encabezados)
const COLOR_TEXT = [40, 40, 40];
const COLOR_MUTED = [120, 120, 120];
const COLOR_BORDER = [180, 180, 180];
const COLOR_ACCENT = [80, 100, 90];      // verde-gris para totales

const fmtCRC = (n) => "₡" + new Intl.NumberFormat("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtNum = (n) => new Intl.NumberFormat("es-CR", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n) || 0);

function tipoPeriodoLabel(kind) {
  if (kind === "mensual") return "Salario Bruto Mensual";
  if (kind === "quincenal") return "Salario Bruto Quincenal";
  return "Salario Bruto Semanal";
}

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const match = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!match) return null;
  const fmt = match[1].toLowerCase();
  return fmt === "jpg" ? "JPEG" : fmt.toUpperCase();
}

export function generarBoletaPago({ empresa, empleado, calc, asistencias = [], rebajos = [], bonificaciones = [], periodoLabel, kind, skipDownload = false, periodEnd = null }) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  registerNomyFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  let y = margin;

  // === CABECERA EMPRESA (banda superior) ===
  doc.setFillColor(...COLOR_TITLE_BG);
  doc.rect(margin, y, pageW - margin * 2, 12, "F");
  doc.setTextColor(...COLOR_TITLE_FG);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(15);
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "Empresa";
  doc.text(empresaNombre, pageW / 2, y + 8, { align: "center" });
  y += 12;

  // === LOGO (esquina superior derecha del bloque siguiente) ===
  const logoFmt = detectImageFormat(empresa?.logo);
  if (logoFmt) {
    try {
      doc.addImage(empresa.logo, logoFmt, pageW - margin - 30, y + 2, 28, 28);
    } catch (e) { /* logo invalid, ignore */ }
  }

  // === Título "COMPROBANTE DE PAGO" + Periodo ===
  y += 4;
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(12);
  doc.text("COMPROBANTE DE PAGO", margin, y + 4);
  y += 7;
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(9);
  doc.text("PERIODO DE PAGO:", margin, y + 4);
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.rect(margin + 40, y, 90, 6, "F");
  doc.setFont(FONT_FAMILY, "bold");
  doc.text(periodoLabel || "—", margin + 40 + 45, y + 4, { align: "center" });
  y += 9;

  // === DATOS EMPLEADO (lado izq) + VALORES HORA (lado der) ===
  const colLW = 78; // ancho columna labels izq
  const colVW = 50; // ancho columna valor izq
  const blockTop = y;
  const rowH = 6.5;

  const empleadoRows = [
    ["Trabajador:", `${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim()],
    ["Cédula de Identidad:", empleado.cedula || "—"],
    ["Número Celular:", empleado.telefono || empleado.celular || "—"],
    ["Correo Electrónico:", empleado.correo || empleado.email || "—"],
    ["Puesto:", empleado.puesto || "—"],
  ];

  // Helper: dibuja texto ajustando el tamaño de fuente para no salirse del
  // ancho disponible. Baja de 8pt hasta 6pt; si aún no cabe, trunca con "…".
  const drawFittedText = (txt, x, yy, maxW, sizeStart = 8, sizeMin = 6) => {
    let sz = sizeStart;
    let str = String(txt);
    doc.setFontSize(sz);
    while (doc.getTextWidth(str) > maxW && sz > sizeMin) {
      sz -= 0.5;
      doc.setFontSize(sz);
    }
    if (doc.getTextWidth(str) > maxW) {
      // Aún no cabe → truncar con ellipsis
      while (str.length > 1 && doc.getTextWidth(str + "…") > maxW) str = str.slice(0, -1);
      str = str + "…";
    }
    doc.text(str, x, yy);
    doc.setFontSize(8); // reset
  };

  empleadoRows.forEach((r, i) => {
    const yy = blockTop + i * rowH;
    // Label
    doc.setFillColor(...COLOR_LABEL_BG);
    doc.rect(margin, yy, 38, rowH, "F");
    doc.setDrawColor(...COLOR_BORDER);
    doc.rect(margin, yy, 38, rowH);
    doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(8);
    doc.text(r[0], margin + 1, yy + 4);
    // Value
    doc.setFillColor(...COLOR_VALUE_BG);
    doc.rect(margin + 38, yy, colLW - 38, rowH, "F");
    doc.rect(margin + 38, yy, colLW - 38, rowH);
    doc.setFont(FONT_FAMILY, "normal");
    // Ancho útil = (colLW - 38) - padding (2mm)
    drawFittedText(r[1] || "—", margin + 39, yy + 4, (colLW - 38) - 2);
  });

  // Tarifas (lado derecho)
  const tarifaX = margin + colLW + 4;
  const tarifaW = pageW - tarifaX - 33 - margin; // dejar 33mm para logo
  const tarifaRows = [
    ["Valor del Día", "Hr Diurna", "Hr Mixta", "Hr Nocturna"],
  ];
  // Detectar tarifas según salario + actividad económica de la empresa
  const salario = Number(empleado.salario_base) || 0;
  const _grupoAct = empresa?.actividad_grupo;
  const _freq = String(empleado.frecuencia_pago || "Mensual").toLowerCase();
  const diasBase = (_grupoAct === "servicios_industria" && _freq === "semanal") ? 26 : 30;
  const valDia = salario / diasBase;
  const hDiurnaOrd = valDia / 8;
  const hMixtaOrd  = valDia / 7;
  const hNocturnaOrd = valDia / 6;
  // Tarifas extras (×1.5)
  const hDiurnaExt = hDiurnaOrd * 1.5;
  const hMixtaExt  = hMixtaOrd * 1.5;
  const hNocturnaExt = hNocturnaOrd * 1.5;

  // Tabla 2 columnas: Tarifa | Ord | Ext
  const tariHeader = ["Concepto", "Ordinaria", "Extra"];
  const tariBody = [
    ["Valor del Día", fmtCRC(valDia), "—"],
    ["Hora Diurna", fmtCRC(hDiurnaOrd), fmtCRC(hDiurnaExt)],
    ["Hora Mixta", fmtCRC(hMixtaOrd), fmtCRC(hMixtaExt)],
    ["Hora Nocturna", fmtCRC(hNocturnaOrd), fmtCRC(hNocturnaExt)],
  ];
  autoTable(doc, {
    startY: blockTop,
    margin: { left: tarifaX },
    tableWidth: tarifaW,
    head: [tariHeader],
    body: tariBody,
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 8, cellPadding: 1.2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
    headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
    columnStyles: { 0: { halign: "left", fillColor: COLOR_LABEL_BG, fontStyle: "bold" }, 1: { halign: "right" }, 2: { halign: "right" } },
  });

  y = Math.max(blockTop + empleadoRows.length * rowH, doc.lastAutoTable.finalY) + 4;

  // === SECCIÓN INFERIOR: Conceptos (izq) + Deducciones (der) ===
  const b = calc.breakdown || {};
  const byOrd = calc.byJornadaOrd;
  const byExt = calc.byJornadaExt;

  // Mapear rebajos por tipo
  const sumByTipo = (tiposArr) => rebajos
    .filter(r => tiposArr.some(t => (r.tipo || "").toLowerCase() === t.toLowerCase()))
    .reduce((s, r) => s + (Number(r.monto) || 0), 0);
  const otherTipos = ["Préstamo", "Caja de Ahorro", "Asociación Solidarista", "Uniforme", "Otro"];
  const pensionMonto   = sumByTipo(["Pensión Alimentaria", "Pension Alimentaria"]);
  const adelantoMonto  = sumByTipo(["Adelanto"]);
  const embargoMonto   = sumByTipo(["Embargo"]);
  const otrasMonto     = sumByTipo(otherTipos);
  const alimentacionMonto = 0; // No tracked separately
  const totalDeduccionesSinCCSS = pensionMonto + adelantoMonto + embargoMonto + alimentacionMonto + otrasMonto;
  const ccssMonto = calc.ccss || 0;
  const conRenta = empresa?.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES;
  const rentaMonto = calc.renta || 0;
  const totalDeducciones = totalDeduccionesSinCCSS + ccssMonto + rentaMonto;

  const bonifTotal = b.bonificaciones?.monto || 0;

  // Columna izquierda: HORAS por jornada + (+) conceptos
  const colLeftBody = [
    ["Horas Diurnas Ordinarias", fmtNum(byOrd.Diurna.hrs), fmtCRC(byOrd.Diurna.monto)],
    ["Horas Mixtas Ordinarias",   fmtNum(byOrd.Mixta.hrs),   fmtCRC(byOrd.Mixta.monto)],
    ["Horas Nocturnas Ordinarias",fmtNum(byOrd.Nocturna.hrs),fmtCRC(byOrd.Nocturna.monto)],
    ["Horas Diurnas Extras",      fmtNum(byExt.Diurna.hrs),  fmtCRC(byExt.Diurna.monto)],
    ["Horas Mixtas Extras",       fmtNum(byExt.Mixta.hrs),   fmtCRC(byExt.Mixta.monto)],
    ["Horas Nocturnas Extras",    fmtNum(byExt.Nocturna.hrs),fmtCRC(byExt.Nocturna.monto)],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin },
    tableWidth: (pageW - margin * 2) / 2 - 2,
    head: [["Detalle de horas", "Cant.", "Monto"]],
    body: colLeftBody,
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 8, cellPadding: 1.2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
    headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold" },
    columnStyles: { 0: { halign: "left" }, 1: { halign: "center", cellWidth: 16 }, 2: { halign: "right", cellWidth: 28 } },
  });
  const yLeftAfter = doc.lastAutoTable.finalY;

  // Columna derecha: Deducciones
  const ccssPct = ((calc?.ccssRate ?? 0.1083) * 100).toFixed(2).replace(/\.?0+$/, "");
  const colRightBody = [
    [`(-) Cargas sociales (${ccssPct}%)`, "", fmtCRC(ccssMonto)],
    ...(conRenta ? [["(-) Impuesto sobre la Renta", "", fmtCRC(rentaMonto)]] : []),
    ["(-) Pensión Alimentaria",      "", fmtCRC(pensionMonto)],
    ["(-) Adelantos de salario",     "", fmtCRC(adelantoMonto)],
    ["(-) Embargos",                 "", fmtCRC(embargoMonto)],
    ["(-) Alimentación",             "", fmtCRC(alimentacionMonto)],
    ["(-) Otras deducciones",        "", fmtCRC(otrasMonto)],
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: pageW / 2 + 1 },
    tableWidth: (pageW - margin * 2) / 2 - 2,
    head: [["Deducciones", "", "Monto"]],
    body: [...colRightBody, ["Total de deducciones:", "", fmtCRC(totalDeducciones)]],
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 8, cellPadding: 1.2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
    headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold" },
    columnStyles: { 0: { halign: "left" }, 1: { cellWidth: 16 }, 2: { halign: "right", cellWidth: 28 } },
    didParseCell: (data) => {
      // Última fila (Total de deducciones): estilo de pie
      if (data.section === "body" && data.row.index === colRightBody.length) {
        data.cell.styles.fillColor = COLOR_ACCENT;
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
  const yRightAfter = doc.lastAutoTable.finalY;

  // Segunda tabla izquierda: (+) conceptos
  const segLeftBody = [
    [`(+) Salario ${kind === "mensual" ? "Mensual" : kind === "quincenal" ? "Quincenal" : "Semanal"}`, "", fmtCRC(calc.montoOrd + calc.montoExt)],
    ["(+) Días Libres",       String(b.diaLibre?.dias || 0),       fmtCRC(b.diaLibre?.monto || 0)],
    ["(+) Días de Vacaciones",String(b.vacaciones?.dias || 0),     fmtCRC(b.vacaciones?.monto || 0)],
    ["(+) Días Feriados",     String(b.feriado?.dias || 0),        fmtCRC(b.feriado?.monto || 0)],
    ["(+) Incapacidades",     String(b.incapacidadCCSS?.dias || 0),fmtCRC(b.incapacidadCCSS?.monto || 0)],
    ["(+) Licencias",         String(b.licencia?.dias || 0),       fmtCRC(b.licencia?.monto || 0)],
    ["(+) Bonificaciones",    String(b.bonificaciones?.cantidad || 0), fmtCRC(bonifTotal)],
  ];
  autoTable(doc, {
    startY: yLeftAfter + 2,
    margin: { left: margin },
    tableWidth: (pageW - margin * 2) / 2 - 2,
    body: segLeftBody,
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 8, cellPadding: 1.2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
    columnStyles: { 0: { halign: "left", fillColor: COLOR_VALUE_BG }, 1: { halign: "center", cellWidth: 16 }, 2: { halign: "right", cellWidth: 28 } },
  });
  const yLeftFinal = doc.lastAutoTable.finalY;

  // Bloque resumen Bruto / Neto a la derecha (usa autotable para garantizar render Unicode)
  const brutoY = yRightAfter + 4;
  const brutoLabel = tipoPeriodoLabel(kind);
  const brutoTableX = pageW / 2 + 1;
  const brutoTableW = (pageW - margin * 2) / 2 - 2;

  autoTable(doc, {
    startY: brutoY,
    margin: { left: brutoTableX },
    tableWidth: brutoTableW,
    head: [[brutoLabel, "Salario Neto a Recibir"]],
    body: [[fmtCRC(calc.totalBruto), fmtCRC(calc.neto)]],
    theme: "grid",
    styles: { font: FONT_FAMILY, fontSize: 9, cellPadding: 2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.2, halign: "center" },
    headStyles: { fillColor: COLOR_ACCENT, textColor: [255,255,255], fontStyle: "bold", halign: "center" },
    bodyStyles: { fontStyle: "bold", fontSize: 12, fillColor: COLOR_VALUE_BG, halign: "center", valign: "middle", minCellHeight: 9 },
  });
  let yBrutoAfter = doc.lastAutoTable.finalY;

  // Salario dividido en dos cuentas: reparto proporcional del bruto, CCSS, rebajos y neto
  const cs = cuentasSalario(empleado, periodEnd);
  if (cs) {
    const [a, b2] = repartirValores([calc.totalBruto, calc.ccss, calc.totalRebajos + rentaMonto, calc.neto], cs.pct1,
      (v) => { v[3] = Math.round((v[0] - v[1] - v[2]) * 100) / 100; return v; });
    const fila = (c, v, pct) => [`${c.nombre} (${(pct * 100).toFixed(1)}%)`, fmtCRC(c.monto), fmtCRC(v[0]), fmtCRC(v[1]), fmtCRC(v[2]), fmtCRC(v[3])];
    autoTable(doc, {
      startY: yBrutoAfter + 2,
      margin: { left: brutoTableX },
      tableWidth: brutoTableW,
      head: [["Cuenta", "Salario", "Bruto", "CCSS", rentaMonto ? "Rebajos/Imp." : "Rebajos", "Neto"]],
      body: [fila(cs.cuentas[0], a, cs.pct1), fila(cs.cuentas[1], b2, 1 - cs.pct1)],
      theme: "grid",
      styles: { font: FONT_FAMILY, fontSize: 6.5, cellPadding: 1, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1, halign: "right" },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" }, 5: { fontStyle: "bold" } },
    });
    yBrutoAfter = doc.lastAutoTable.finalY;
  }

  // Observaciones con detalle de rebajos (sin CCSS) + bonificaciones aplicadas
  const obsY = yBrutoAfter + 4;
  doc.setTextColor(...COLOR_TEXT); doc.setFontSize(9); doc.setFont(FONT_FAMILY, "bold");
  doc.text("Observaciones:", brutoTableX, obsY);

  // Construir líneas
  const obsLines = [];
  const rebajosNoCCSS = (rebajos || []).filter(r => Number(r.monto) > 0);
  if (rebajosNoCCSS.length > 0) {
    obsLines.push("Rebajos aplicados:");
    for (const r of rebajosNoCCSS) {
      const desc = r.descripcion ? ` — ${r.descripcion}` : "";
      obsLines.push(`  • ${r.tipo || "Rebajo"}${desc}: ${fmtCRC(r.monto)}`);
    }
  }
  const bonifsActivas = (bonificaciones || []).filter(b => Number(b.monto) > 0);
  if (bonifsActivas.length > 0) {
    obsLines.push("Bonificaciones aplicadas:");
    for (const b of bonifsActivas) {
      const desc = b.descripcion ? ` — ${b.descripcion}` : (b.concepto ? ` — ${b.concepto}` : "");
      obsLines.push(`  • ${b.tipo || "Bonificación"}${desc}: ${fmtCRC(b.monto)}`);
    }
  }

  // Calcular altura dinámica del cuadro
  const obsLineHeight = 3.6;
  const obsBoxPadTop = 2;
  const obsBoxPadBottom = 2;
  const obsBoxMinH = 18;
  const obsBoxH = Math.max(obsBoxMinH, obsLines.length * obsLineHeight + obsBoxPadTop + obsBoxPadBottom);
  doc.setDrawColor(...COLOR_BORDER);
  doc.setFillColor(255,255,255);
  doc.rect(brutoTableX, obsY + 1, brutoTableW, obsBoxH, "FD");

  // Dibujar líneas de observaciones
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7.5); doc.setTextColor(...COLOR_TEXT);
  obsLines.forEach((line, i) => {
    const isHeader = !line.startsWith("  ");
    doc.setFont(FONT_FAMILY, isHeader ? "bold" : "normal");
    doc.text(line, brutoTableX + 2, obsY + 1 + obsBoxPadTop + (i + 1) * obsLineHeight - 0.5);
  });

  y = Math.max(yLeftFinal, obsY + obsBoxH + 4);

  // Recuadro con el cálculo del Impuesto sobre la Renta
  const rd = conRenta ? calc.rentaDetalle : null;
  if (rd) {
    const filas = [["Bruto mensual considerado", fmtCRC(rd.base)]];
    if (rd.modo === "quincena") filas.push(["(Proyección: bruto de la quincena × 2)", ""]);
    if (rd.modo === "segunda") filas.push([`(I quincena ${fmtCRC(rd.brutoPrimera)} + II quincena)`, ""]);
    rd.tramos.forEach(t => filas.push([`Tramo ${t.tarifa}% sobre ${fmtCRC(t.sobre)}`, fmtCRC(t.monto)]));
    filas.push(["Impuesto bruto", fmtCRC(rd.impuestoBruto)]);
    if (rd.creditoConyuge) filas.push(["(-) Crédito cónyuge", fmtCRC(rd.creditoConyuge)]);
    if (rd.creditoHijos) filas.push([`(-) Créditos por ${rd.hijos} hijo${rd.hijos === 1 ? "" : "s"}`, fmtCRC(rd.creditoHijos)]);
    filas.push(["Impuesto del mes", fmtCRC(rd.impuestoMes)]);
    filas.push([kind === "mensual" ? "Rebajo en este mes" : "Rebajo en esta quincena", fmtCRC(rentaMonto)]);
    if (y + filas.length * 4 + 10 > pageH - 30) { doc.addPage(); y = margin; }
    autoTable(doc, {
      startY: y,
      margin: { left: brutoTableX },
      tableWidth: brutoTableW,
      head: [["Cálculo del Impuesto sobre la Renta", ""]],
      body: filas,
      theme: "grid",
      styles: { font: FONT_FAMILY, fontSize: 7, cellPadding: 1, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold" },
      columnStyles: { 0: { halign: "left" }, 1: { halign: "right", cellWidth: 28 } },
      didParseCell: (data) => { if (data.section === "body" && data.row.index >= filas.length - 2) data.cell.styles.fontStyle = "bold"; },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // === RESUMEN DE DÍAS TRABAJADOS (sin montos) ===
  const ocultarResumenDias = empresa?.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES;
  if (!ocultarResumenDias && asistencias && asistencias.length > 0) {
    y += 4;
    if (y + 20 > pageH - 30) { doc.addPage(); y = margin; }
    doc.setTextColor(...COLOR_TEXT);
    doc.setFont(FONT_FAMILY,"bold"); doc.setFontSize(10);
    doc.text("Resumen de días trabajados", margin, y);
    y += 2;

    const ccssDayMap = buildCCSSDayMap(asistencias);
    // Dedup por fecha: si por algún motivo llegan varios docs para el mismo
    // día (bug de datos legacy o guardado incompleto), conservamos el "más
    // completo": el que tenga salida > el que tenga entrada > el primero.
    const byFecha = new Map();
    for (const r of asistencias) {
      const key = r.fecha || "";
      if (!key) continue;
      const prev = byFecha.get(key);
      if (!prev) { byFecha.set(key, r); continue; }
      const scoreOf = (x) => (x?.salida ? 2 : 0) + (x?.entrada ? 1 : 0) + ((x?.horas_ordinarias || 0) + (x?.horas_extras || 0) > 0 ? 1 : 0);
      if (scoreOf(r) > scoreOf(prev)) byFecha.set(key, r);
    }
    const sorted = Array.from(byFecha.values()).sort((a, b) => (a.fecha || "").localeCompare(b.fecha || ""));
    const DIA_SEMANA = ["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"];
    const hrsList = computeRecordsHours(sorted, empleado, ccssDayMap, aplicaJornada48(empresa, empleado));
    const body = sorted.map((r, idx) => {
      const h = hrsList[idx];
      const f = r.fecha ? new Date(r.fecha + "T00:00:00") : null;
      const ds = f ? DIA_SEMANA[f.getDay()] : "";
      return [
        r.fecha || "",
        ds,
        r.estado || "Presente",
        r.entrada || "—",
        r.salida || "—",
        h.jornada || (r.estado && r.estado !== "Presente" ? "—" : (empleado.jornada || "Diurna")),
        h.ord ? fmtNum(h.ord) : "0",
        h.ext ? fmtNum(h.ext) : "0",
      ];
    });
    autoTable(doc, {
      startY: y + 2,
      margin: { left: margin, right: margin },
      head: [["Fecha", "Día", "Estado", "Entrada", "Salida", "Jornada", "Hrs Ord", "Hrs Ext"]],
      body,
      theme: "grid",
      styles: { font: FONT_FAMILY, fontSize: 8, cellPadding: 1.2, textColor: COLOR_TEXT, lineColor: COLOR_BORDER, lineWidth: 0.1 },
      headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
      columnStyles: {
        0: { halign: "center", cellWidth: 22 },
        1: { halign: "center", cellWidth: 12 },
        2: { halign: "left" },
        3: { halign: "center", cellWidth: 18 },
        4: { halign: "center", cellWidth: 18 },
        5: { halign: "center", cellWidth: 20 },
        6: { halign: "right", cellWidth: 18 },
        7: { halign: "right", cellWidth: 18 },
      },
    });
    y = doc.lastAutoTable.finalY;
  }

  // === Nota legal de Licencia (Art. 95 CT) cuando aplique ===
  const diasLicencia = b.licencia?.dias || 0;
  if (diasLicencia > 0) {
    const txt = `Conforme al Art. 95 del Código de Trabajo, durante los ${diasLicencia} día${diasLicencia === 1 ? "" : "s"} de licencia el patrono aportó el 50 % del salario (${fmtCRC(b.licencia.monto)}) y la CCSS cubre el 50 % restante directamente al trabajador.`;
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(7);
    const lineas = doc.splitTextToSize(txt, pageW - margin * 2 - 17);
    const altura = Math.max(10, 3 + lineas.length * 3.2);
    const notaY = Math.min(y + 4, pageH - 40);
    doc.setFillColor(248, 244, 234);
    doc.setDrawColor(180, 160, 120);
    doc.setLineWidth(0.2);
    doc.rect(margin, notaY, pageW - margin * 2, altura, "FD");
    doc.setTextColor(120, 90, 30);
    doc.setFont(FONT_FAMILY, "bold");
    doc.setFontSize(7.5);
    doc.text("Nota legal:", margin + 2, notaY + 4);
    doc.setTextColor(...COLOR_TEXT);
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(7);
    lineas.forEach((line, i) => {
      doc.text(line, margin + 15, notaY + 4 + i * 3.2);
    });
    y = notaY + altura + 2;
  }

  // === FIRMAS (al pie) — 2 espacios con nombres ===
  let firmasY = Math.max(y + 18, pageH - 32);
  if (firmasY + 20 > pageH - margin) { doc.addPage(); firmasY = pageH - 40; }
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.3);
  const firmaW = 70;
  const firma1X = pageW * 0.25 - firmaW / 2;
  const firma2X = pageW * 0.75 - firmaW / 2;
  doc.line(firma1X, firmasY, firma1X + firmaW, firmasY);
  doc.line(firma2X, firmasY, firma2X + firmaW, firmasY);

  // Nombres y leyendas debajo de cada firma
  const empleadoNombre = `${empleado.nombre || ""} ${empleado.apellidos || ""}`.trim() || "—";
  const empresaNombreFirma = empresa?.razon_social || empresa?.nombre || "—";
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold"); doc.setFontSize(9);
  doc.text(empleadoNombre,     pageW * 0.25, firmasY + 4.5, { align: "center" });
  doc.text(empresaNombreFirma, pageW * 0.75, firmasY + 4.5, { align: "center" });
  doc.setFont(FONT_FAMILY, "normal"); doc.setFontSize(7.5); doc.setTextColor(...COLOR_MUTED);
  doc.text("Firma del Trabajador",       pageW * 0.25, firmasY + 8.5, { align: "center" });
  doc.text("Firma por la Empresa",       pageW * 0.75, firmasY + 8.5, { align: "center" });

  // Pie de página
  doc.setFont(FONT_FAMILY,"normal"); doc.setFontSize(7); doc.setTextColor(...COLOR_MUTED);
  const fechaGen = new Date().toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(`Generado el ${fechaGen} · ${empresa?.razon_social || empresa?.nombre || ""}`, pageW - margin, pageH - 5, { align: "right" });

  // Nombre archivo
  const safeName = `${empleado.nombre || ""}_${empleado.apellidos || ""}`.replace(/\s+/g, "_");
  const fileName = `Comprobante_${safeName}_${(periodoLabel || "").replace(/[^\w-]/g, "_")}.pdf`;
  if (!skipDownload) doc.save(fileName);

  // Devolver dataURL base64 para guardado en backend
  const pdfDataUri = doc.output("datauristring");
  const pdfBase64 = pdfDataUri.split(",")[1];
  return { fileName, pdfBase64 };
}
